import { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';
import { FormControl } from 'mk2/components/forms/FormControl';
import { Btn, BtnType } from 'mk2/components/Btn';
import React from 'react';
import { Field, WrappedFieldInputProps, WrappedFieldProps } from 'redux-form';
import styles from './ClickableField.mscss';

export enum HideArrow {
    Never,
    IfValue, // hide arrow icon if component has value
    Always,
}

interface ClickableInputPublicProps {
    ValueComponent?: React.ComponentType<{ value: any; input?: WrappedFieldInputProps; disabled?: boolean }>;
    onClick?: React.MouseEventHandler<any>;
    hideArrow?: HideArrow;
    bottomBorder?: boolean;
    disabled?: boolean;
    arrowIcon?: IconLookup;
}

type ClickableInputProps = ClickableInputPublicProps & {
    hasLabel: boolean;
    input: WrappedFieldInputProps;
};

const ClickableInput: React.FC<ClickableInputProps> = ({
    ValueComponent,
    input,
    onClick,
    hasLabel,
    hideArrow = HideArrow.Never,
    disabled,
    arrowIcon = faAngleRight,
}) => {
    let showArrow = true;
    if (hideArrow === HideArrow.IfValue) {
        if (!!input.value) {
            showArrow = false;
        }
    } else if (hideArrow === HideArrow.Always) {
        showArrow = false;
    }

    return (
        <div
            id={input.name}
            className={cx(
                styles.ClickableInput,
                !disabled && styles['ClickableInput--enabled'],
                !hasLabel && styles['ClickableInput--noLabel'],
            )}
            onClick={disabled ? undefined : onClick}
        >
            <div
                className={cx(
                    styles.ClickableInput__valuePreview,
                    !hasLabel && styles['ClickableInput__valuePreview--noLabel'],
                    disabled && styles['ClickableInput__valuePreview--disabled'],
                )}
            >
                {
                    ValueComponent ? (
                        <ValueComponent value={input.value} input={input} disabled={disabled} />
                    ) : (
                        String(input.value)
                    ) // nech vidno, ze treba asi customizovat render pre preview hodnoty
                }
            </div>

            {showArrow && (
                <Btn
                    type={BtnType.BlueTextLight}
                    icon={arrowIcon}
                    className={cx(styles.ClickableInput__icon, disabled && styles['ClickableInput__icon--disabled'])}
                />
            )}
        </div>
    );
};

interface ClickableControlPublicProps {
    label?: string;
    marginLeft?: boolean;
    minimizedLabel?: boolean;
}

type ClickableControlProps = ClickableInputPublicProps & ClickableControlPublicProps & WrappedFieldProps;

const ClickableControl: React.FC<ClickableControlProps> = ({
    input,
    meta,
    label, // WrappedFieldProps
    marginLeft,
    bottomBorder = true, // ClickableControlPublicProps
    minimizedLabel = false, // ClickableControlPublicProps
    ...clickableInputProps // ClickableInputPublicProps
}) => (
    <FormControl
        input={input}
        label={label}
        meta={meta}
        marginLeft={marginLeft}
        bottomBorder={bottomBorder}
        disabled={clickableInputProps.disabled}
        minimizedLabel={minimizedLabel}
    >
        <ClickableInput {...clickableInputProps} input={input} hasLabel={!!label} />
    </FormControl>
);

type ClickableFieldProps = ClickableInputPublicProps &
    ClickableControlPublicProps & {
        // see https://redux-form.com/7.3.0/docs/api/field.md/#props-you-can-pass-to-code-field-code-
        name: string;
    };

export const ClickableField: React.FC<ClickableFieldProps> = (props) => <Field {...props} component={ClickableControl} />;
